import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addAssignmentsAPI,
  updateAssignmentsAPI,
  getAssignmentsListAPI,
  getAssignmentsDetailAPI,
  deleteAssignmentsAPI,
  archiveAssignmentsAPI, // Import the new API
} from "./assignmentsAPI";

// ✅ Add
export const addAssignments = createAsyncThunk(
  "assignments/add",
  async (data, { rejectWithValue }) => {
    try { 
      const response = await addAssignmentsAPI(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add Assignment");
    }
  }
);

// ✅ Update
export const updateAssignments = createAsyncThunk(
  "assignments/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateAssignmentsAPI({ id, data });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update Assignment");
    }
  }
);

// ✅ Archive (New Thunk)
export const archiveAssignments = createAsyncThunk(
  "assignments/archive",
  async ({ id, is_archive }, { rejectWithValue }) => {
    try {
      const response = await archiveAssignmentsAPI({ id, is_archive });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive Assignment");
    }
  }
);

// ✅ List (Updated to pass archived param)
export const getAssignmentsList = createAsyncThunk(
  "assignments/list",
  async (params, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search = "", access_group_id = [], archived } = params || {};
      const response = await getAssignmentsListAPI({ page, limit, search, access_group_id, archived });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch Assignment list");
    }
  }
);

// ✅ Detail
export const getAssignmentsDetail = createAsyncThunk(
  "assignments/detail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getAssignmentsDetailAPI(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch Assignment detail");
    }
  }
);

// ✅ Delete
export const deleteAssignments = createAsyncThunk(
  "assignments/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteAssignmentsAPI(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete Assignment");
    }
  }
);

const initialState = {
  addAssignments: { data: null, loading: false, error: null },
  updateAssignments: { data: null, loading: false, error: null },
  archiveAssignments: { data: null, loading: false, error: null }, // Added state for archive
  getAssignmentsList: { data: [], total: 0, loading: false, error: null },
  getAssignmentsDetail: { data: null, loading: false, error: null },
  deleteAssignments: { data: null, loading: false, error: null },
};

const assignmentsSlice = createSlice({
  name: "assignments",
  initialState,
  reducers: {
    clearAddAssignments: (state) => {
      state.addAssignments.data = null;
      state.addAssignments.error = null;
    },
    clearUpdateAssignments: (state) => {
      state.updateAssignments.data = null;
      state.updateAssignments.error = null;
    },
    clearArchiveAssignments: (state) => { // Added clear reducer
      state.archiveAssignments.data = null;
      state.archiveAssignments.error = null;
    },
    clearAssignmentsDetail: (state) => {
      state.getAssignmentsDetail.data = null;
      state.getAssignmentsDetail.error = null;
    },
    clearDeleteAssignments: (state) => {
      state.deleteAssignments.data = null;
      state.deleteAssignments.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add
      .addCase(addAssignments.pending, (state) => {
        state.addAssignments.loading = true;
        state.addAssignments.error = null;
      })
      .addCase(addAssignments.fulfilled, (state, action) => {
        state.addAssignments.loading = false;
        state.addAssignments.data = action.payload;
      })
      .addCase(addAssignments.rejected, (state, action) => {
        state.addAssignments.loading = false;
        state.addAssignments.error = action.payload;
      })

      // Update
      .addCase(updateAssignments.pending, (state) => {
        state.updateAssignments.loading = true;
      })
      .addCase(updateAssignments.fulfilled, (state, action) => {
        state.updateAssignments.loading = false;
        state.updateAssignments.data = action.payload;
      })
      .addCase(updateAssignments.rejected, (state, action) => {
        state.updateAssignments.loading = false;
        state.updateAssignments.error = action.payload;
      })

      // Archive (New Cases)
      .addCase(archiveAssignments.pending, (state) => {
        state.archiveAssignments.loading = true;
        state.archiveAssignments.error = null;
      })
      .addCase(archiveAssignments.fulfilled, (state, action) => {
        state.archiveAssignments.loading = false;
        state.archiveAssignments.data = action.payload;
      })
      .addCase(archiveAssignments.rejected, (state, action) => {
        state.archiveAssignments.loading = false;
        state.archiveAssignments.error = action.payload;
      })

      // List
      .addCase(getAssignmentsList.pending, (state) => {
        state.getAssignmentsList.loading = true;
      })
      .addCase(getAssignmentsList.fulfilled, (state, action) => {
        state.getAssignmentsList.loading = false;
        state.getAssignmentsList.data = action.payload?.data || [];
        state.getAssignmentsList.total =
          action.payload?.total || action.payload?.count || (action.payload?.data ? action.payload.data.length : 0);
      })
      .addCase(getAssignmentsList.rejected, (state, action) => {
        state.getAssignmentsList.loading = false;
        state.getAssignmentsList.error = action.payload;
      })

      // Detail
      .addCase(getAssignmentsDetail.pending, (state) => {
        state.getAssignmentsDetail.loading = true;
      })
      .addCase(getAssignmentsDetail.fulfilled, (state, action) => {
        state.getAssignmentsDetail.loading = false;
        state.getAssignmentsDetail.data = action.payload?.data || action.payload;
      })
      .addCase(getAssignmentsDetail.rejected, (state, action) => {
        state.getAssignmentsDetail.loading = false;
        state.getAssignmentsDetail.error = action.payload;
      })

      // Delete
      .addCase(deleteAssignments.pending, (state) => {
        state.deleteAssignments.loading = true;
        state.deleteAssignments.error = null;
      })
      .addCase(deleteAssignments.fulfilled, (state, action) => {
        state.deleteAssignments.loading = false;
        state.deleteAssignments.data = action.payload?.data || action.payload;
      })
      .addCase(deleteAssignments.rejected, (state, action) => {
        state.deleteAssignments.loading = false;
        state.deleteAssignments.error = action.payload;
      });
  },
});

export const {
  clearAddAssignments,
  clearUpdateAssignments,
  clearArchiveAssignments,
  clearAssignmentsDetail,
  clearDeleteAssignments,
} = assignmentsSlice.actions;

export default assignmentsSlice.reducer;